import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import type { ReactNode } from "react";
import type { Plan, Site } from "./database.types";
import { apiFetch, ApiError } from "./api";

// SitesContext holds the caller's sites and the currently-selected one. The
// selection persists in localStorage and drives all site-scoped views (M2+). The
// rest of the app reads the selected site id from here — every future data query
// is keyed on it.

const SELECTED_KEY = "citada.site";

export interface CreateSiteInput {
  domain: string;
  description?: string;
  plan?: Plan;
}

interface SitesContextValue {
  sites: Site[];
  loading: boolean;
  error: string | null;
  selectedSite: Site | null;
  hasFreeSite: boolean;
  selectSite: (id: string) => void;
  refresh: () => Promise<void>;
  createSite: (input: CreateSiteInput) => Promise<Site>;
  updateSite: (id: string, patch: { plan?: Plan; cadence?: string }) => Promise<Site>;
}

const SitesContext = createContext<SitesContextValue | null>(null);

export function SitesProvider({
  token,
  children,
}: {
  token: string;
  children: ReactNode;
}) {
  const [sites, setSites] = useState<Site[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(
    () => localStorage.getItem(SELECTED_KEY),
  );

  const refresh = useCallback(async () => {
    try {
      const res = await apiFetch<{ sites: Site[] }>("/api/sites", token);
      setSites(res.sites);
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.code : "load_failed");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Keep the selection valid: if the stored id isn't in the list (deleted, other
  // device, first load), fall back to the first site.
  useEffect(() => {
    if (loading) return;
    if (sites.length === 0) {
      if (selectedId !== null) setSelectedId(null);
      return;
    }
    if (!selectedId || !sites.some((s) => s.id === selectedId)) {
      setSelectedId(sites[0].id);
    }
  }, [loading, sites, selectedId]);

  useEffect(() => {
    if (selectedId) localStorage.setItem(SELECTED_KEY, selectedId);
    else localStorage.removeItem(SELECTED_KEY);
  }, [selectedId]);

  const selectSite = useCallback((id: string) => setSelectedId(id), []);

  const createSite = useCallback(
    async (input: CreateSiteInput) => {
      const res = await apiFetch<{ site: Site }>("/api/sites", token, {
        method: "POST",
        body: input,
      });
      await refresh();
      setSelectedId(res.site.id);
      return res.site;
    },
    [token, refresh],
  );

  const updateSite = useCallback(
    async (id: string, patch: { plan?: Plan; cadence?: string }) => {
      const res = await apiFetch<{ site: Site }>(`/api/sites/${id}`, token, {
        method: "PATCH",
        body: patch,
      });
      await refresh();
      return res.site;
    },
    [token, refresh],
  );

  const selectedSite = useMemo(
    () => sites.find((s) => s.id === selectedId) ?? null,
    [sites, selectedId],
  );
  const hasFreeSite = useMemo(() => sites.some((s) => s.plan === "free"), [sites]);

  const value: SitesContextValue = {
    sites,
    loading,
    error,
    selectedSite,
    hasFreeSite,
    selectSite,
    refresh,
    createSite,
    updateSite,
  };

  return <SitesContext.Provider value={value}>{children}</SitesContext.Provider>;
}

export function useSites(): SitesContextValue {
  const ctx = useContext(SitesContext);
  if (!ctx) throw new Error("useSites must be used within a SitesProvider");
  return ctx;
}
